import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Cel,
  Foutmelding,
  Kaart,
  Knop,
  Leeg,
  Modaal,
  PaginaKop,
  Rij,
  SkeletTabel,
  Tabel,
  Veld,
  useToast
} from '../components/ui';
import { IcoonBeker, IcoonPlus } from '../components/icons';
import { gespeeldePartijen, maakSchema, teamSleutel } from '../lib/bracket';
import { maakDuos } from '../lib/prestaties';
import type { Mode } from '../lib/types';
import { fmtDatum, toonNaam, toonRating } from '../lib/format';
import { useLeague, useSaveTournament } from '../lib/queries';
import { usePoule } from '../lib/poule';

export default function Toernooi() {
  const { pouleId } = usePoule();
  const { tournaments, zoekSpeler, laden } = useLeague(pouleId);
  const [nieuw, setNieuw] = useState(false);
  const navigeer = useNavigate();

  if (laden) {
    return (
      <>
        <PaginaKop titel="Toernooi" />
        <Kaart>
          <SkeletTabel rijen={3} kolommen={5} />
        </Kaart>
      </>
    );
  }

  const lijst = tournaments.slice().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <>
      <PaginaKop
        titel="Toernooi"
        onder="Enkelvoudig afvalschema. Deelnemers worden geplaatst op rating: de sterkste treft de zwakste."
        rechts={
          <Knop variant="primair" onClick={() => setNieuw(true)}>
            <IcoonPlus /> Nieuw toernooi
          </Knop>
        }
      />

      <Kaart>
        {lijst.length ? (
          <Tabel koppen={['Toernooi', 'Datum', 'Deelnemers', 'Status', '']}>
            {lijst.map((t) => (
              <Rij key={t.id}>
                <Cel>
                  <strong>{t.name}</strong>
                </Cel>
                <Cel>{fmtDatum(t.createdAt)}</Cel>
                <Cel nummer>{t.participants.length}</Cel>
                <Cel>
                  {t.status === 'klaar' && t.championId ? (
                    <span className="flex items-center gap-1.5">
                      <IcoonBeker className="text-goud" /> gewonnen door {toonNaam(zoekSpeler(t.championId))}
                    </span>
                  ) : (
                    <Badge>bezig · {gespeeldePartijen(t)} potjes gespeeld</Badge>
                  )}
                </Cel>
                <Cel rechts>
                  <Knop klein onClick={() => navigeer(`/toernooi/${t.id}`)}>
                    Openen
                  </Knop>
                </Cel>
              </Rij>
            ))}
          </Tabel>
        ) : (
          <Leeg tekst="Nog geen toernooi gespeeld." ikoon="beker" />
        )}
      </Kaart>

      {nieuw ? <NieuwToernooiModaal onSluit={() => setNieuw(false)} /> : null}
    </>
  );
}

function NieuwToernooiModaal({ onSluit }: { onSluit: () => void }) {
  const { pouleId } = usePoule();
  const { actief, rating, zoekSpeler } = useLeague(pouleId);
  const bewaren = useSaveTournament();
  const meld = useToast();
  const navigeer = useNavigate();

  const kandidaten = actief.slice().sort((a, b) => rating(b.id) - rating(a.id));
  const [naam, setNaam] = useState(`Toernooi ${fmtDatum(new Date())}`);
  const [gekozen, setGekozen] = useState<string[]>(kandidaten.map((p) => p.id));
  const [vorm, setVorm] = useState<Mode>('1v1');
  const [duos, setDuos] = useState<string[][] | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const duoVorm = vorm === '2v2';
  /* Zolang je nog niet zelf hebt geloot: de gebalanceerde koppeling. */
  const huidigeDuos = duoVorm ? (duos ?? maakDuos(gekozen, rating)) : [];

  async function maak() {
    if (!naam.trim()) {
      setFout('Geef het toernooi een naam.');
      return;
    }
    if (duoVorm) {
      if (gekozen.length < 4) {
        setFout('Voor een duotoernooi zijn minstens vier spelers nodig.');
        return;
      }
      if (gekozen.length % 2 !== 0) {
        setFout('Kies een even aantal spelers; iedereen moet een ploeggenoot hebben.');
        return;
      }
    } else if (gekozen.length < 2) {
      setFout('Kies minstens twee deelnemers.');
      return;
    }

    /* Bij 2v2 speelt het schema met duo-sleutels (t0, t1, ...) en telt het
       gemiddelde van het duo als rating. Bij 1v1 blijft alles zoals het was. */
    const sleutels = duoVorm ? huidigeDuos.map((_, i) => teamSleutel(i)) : gekozen;
    const sleutelRating = duoVorm
      ? (s: string) => {
          const duo = huidigeDuos[Number(s.slice(1))] ?? [];
          return duo.length ? duo.reduce((n, id) => n + rating(id), 0) / duo.length : 0;
        }
      : rating;

    const toernooi = maakSchema(sleutels, sleutelRating, naam.trim(), pouleId, {
      mode: vorm,
      teams: duoVorm ? huidigeDuos : null,
      participants: duoVorm ? huidigeDuos.flat() : gekozen.slice()
    });
    try {
      await bewaren.mutateAsync(toernooi);
      meld(
        duoVorm
          ? `Schema gemaakt voor ${huidigeDuos.length} duo's.`
          : `Schema gemaakt voor ${gekozen.length} deelnemers.`
      );
      onSluit();
      navigeer(`/toernooi/${toernooi.id}`);
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Opslaan mislukte.');
    }
  }

  return (
    <Modaal
      titel="Nieuw toernooi"
      onSluit={onSluit}
      voet={
        <>
          <Knop onClick={onSluit}>Annuleren</Knop>
          <Knop variant="primair" onClick={() => void maak()} disabled={bewaren.isPending}>
            {bewaren.isPending ? 'Bezig…' : 'Schema maken'}
          </Knop>
        </>
      }
    >
      {fout ? <Foutmelding>{fout}</Foutmelding> : null}

      <Veld label="Naam">
        <input className="veld" value={naam} onChange={(e) => setNaam(e.target.value)} />
      </Veld>

      <Veld label="Speelvorm">
        <div className="flex gap-2">
          {(['1v1', '2v2'] as Mode[]).map((m) => (
            <button
              key={m}
              onClick={() => {
                setVorm(m);
                setDuos(null);
                setFout(null);
              }}
              className={`rounded-sm border px-3 py-1.5 text-sm transition ${
                vorm === m
                  ? 'border-veld-700 bg-veld-50 font-semibold text-veld-900'
                  : 'border-krijt-300 hover:bg-krijt-100'
              }`}
            >
              {m === '1v1' ? 'Eén tegen één' : 'Twee tegen twee'}
            </button>
          ))}
        </div>
      </Veld>

      <Veld label="Deelnemers" help="Het schema wordt aangevuld met byes tot 2, 4, 8 of 16 plekken.">
        <div className="max-h-64 overflow-auto rounded-md border border-krijt-200 p-1">
          {kandidaten.map((p) => (
            <label key={p.id} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-krijt-50">
              <input
                type="checkbox"
                checked={gekozen.includes(p.id)}
                onChange={(e) =>
                  setGekozen((oud) => (e.target.checked ? [...oud, p.id] : oud.filter((id) => id !== p.id)))
                }
              />
              <Avatar speler={p} maat="sm" />
              <span className="text-sm">{toonNaam(p)}</span>
              <span className="nums ml-auto text-xs text-krijt-500">{toonRating(rating(p.id))}</span>
            </label>
          ))}
        </div>
      </Veld>

      {duoVorm ? (
        <Veld
          label="De duo's"
          help="Standaard wordt de sterkste aan de zwakste gekoppeld, zodat de duo's dicht bij elkaar liggen."
        >
          {gekozen.length >= 4 && gekozen.length % 2 === 0 ? (
            <>
              <div className="flex flex-col gap-1.5">
                {huidigeDuos.map((duo, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 rounded-sm border border-krijt-200 px-2.5 py-1.5 text-sm"
                  >
                    <span className="nums w-5 text-xs text-krijt-400">{i + 1}</span>
                    <span>{duo.map((id) => toonNaam(zoekSpeler(id))).join(' & ')}</span>
                    <span className="nums ml-auto text-xs text-krijt-500">
                      {toonRating(duo.reduce((n, id) => n + rating(id), 0) / duo.length)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <Knop onClick={() => setDuos(maakDuos(gekozen, rating, true))}>Opnieuw loten</Knop>
                <Knop onClick={() => setDuos(null)}>Op sterkte koppelen</Knop>
              </div>
            </>
          ) : (
            <p className="text-sm text-krijt-500">
              Kies een even aantal spelers, minstens vier, dan verschijnen hier de duo's.
            </p>
          )}
        </Veld>
      ) : null}
    </Modaal>
  );
}
